import { EmptyState } from "@rio-cloud/rio-uikit";
import { useSelectAssetsStore } from "../../services/selectAssetsStore";
import {
  Spinner,
  StatsWidget,
  StatsWidgetNumber,
  StatsWidgetSpacer,
  StatsWidgets,
} from "@rio-cloud/rio-uikit";
import Notification from '@rio-cloud/rio-uikit/Notification';
import { type CSSProperties, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useFetcher } from "react-router";
import { AlertsTable } from "../../features/alertsTable";
import { useRefresh } from "../../shared/utils";
import { alertAction } from "./action";
import { Alert } from "../../shared/models/Alert";
import { usePollingInterceptor } from "../../features/alertsTable/hooks/useSoftFailurePolling";

function TextWithSpinner({
  children,
  isLoading,
}: {
  children: ReactNode;
  isLoading: boolean;
}) {
  return (
    <div
      style={{
        maxWidth: "fit-content",
        marginInline: "auto",
        position: "relative",
        display: "flex",
        alignItems: "center",
      }}
    >
      <span>{children}</span>

      <span
        style={{
          right: -30,
        }}
        className="position-absolute"
      >
        {isLoading && <Spinner isInline />}
      </span>
    </div>
  );
}

function AlertsPage() {
  const {selectedAssets} = useSelectAssetsStore();

  const memoSelectedAssets = useMemo(() => selectedAssets, [selectedAssets]);
  const fetcher = useFetcher<typeof alertAction>();
  const { isSoftErrorVisible, retryCount } = usePollingInterceptor(fetcher);
  const [filter, setFilter] = useState<"attention" | "instability" | "operationalInsistence" | null>(
    null
  );
  

  const isLoading = fetcher.state !== "idle";

  const handleSubmit = useCallback(
    (payload: string[]) => {
      fetcher.submit(payload, {
        method: "post",
        encType: "application/json",
      });
    },
    [fetcher.submit]
  );

  useEffect(() => {
    handleSubmit(memoSelectedAssets)
  }, [memoSelectedAssets, handleSubmit])
  

  const handleResetFilters = () => setFilter(null);
  useRefresh({
    callback: () => handleSubmit(memoSelectedAssets),
    delay: 60_000,
  });

  useEffect(() => {
    if (isSoftErrorVisible) {
      Notification.error('Erro ao buscar informações de alertas. Tentando novamente...');
    }
  }, [isSoftErrorVisible, retryCount]);

  const initialValues = {
    instability: 0,
    attention: 0,
    operational_insistence: 0,
  };

  const isDataReady = Array.isArray(fetcher.data);

  const { instability, attention, operational_insistence } = isDataReady
  ? (fetcher.data as Alert[]).reduce((acc, current) => {
      if (current?.alert === "attention") {
        acc.attention += 1;
      } else if (current?.alert === "instability") {
        acc.instability += 1;
      } else if (current.alert === "operationalInsistence") {
          acc.operational_insistence += 1;
      }
      return acc;
    }, initialValues)
  : initialValues;

  const filteredAlerts = fetcher.data
    ? filter
      ? (fetcher.data as Alert[]).filter((entry) => entry.alert === filter)
      : fetcher.data
    : [];

  if (selectedAssets.length === 0) {
    return (
       <EmptyState
        fullWidth
        headline={
          <TextWithSpinner isLoading={isLoading}>
            Nada selecionado
          </TextWithSpinner>
        }
        message="Selecione um veículo ou grupo para exibir alertas."
      />
    );
  }
  return (
      <div
      style={
        {
          "--navbar-height": "50px",
          "--body-padding": "30px",
          maxHeight:
            "calc(100svh - var(--navbar-height) - var(--body-padding) * 2)",
          gridTemplateRows: "auto 1fr",
        } as CSSProperties
      }
      className="display-grid gap-20"
    >
      <StatsWidgets>
        <StatsWidget
          hasFilter={filter != null}
          onFilterReset={handleResetFilters}
        >
          <StatsWidget.Header>
            <TextWithSpinner isLoading={isLoading}>
              Monitor de Alertas • Últimas 24h
            </TextWithSpinner>
          </StatsWidget.Header>
          <StatsWidget.Body>
            {/* <StatsWidgetNumber
              key={`attention-${attention}`}
              value={attention}
              label="Condução Desatenta"
              onClick={() =>
                setFilter(filter === "attention" ? null : "attention")
              }
              clickable
              className={[
                "text-color-map-marker-poi",
                filter !== null && filter !== "attention" ? "opacity-50" : "",
                attention > 0 ? "animate-highlight" : "",
              ].join(" ")}
            />
            <StatsWidgetSpacer />
            <StatsWidgetNumber
              key={`instability-${instability}`}
              value={instability}
              label="Condição de Instabilidade"
              onClick={() =>
                setFilter(filter === "instability" ? null : "instability")
              }
              clickable
              className={[
                "text-color-primary",
                filter !== null && filter !== "instability" ? "opacity-50" : "",
                instability > 0 ? "animate-highlight" : "",
              ].join(" ")}
            />
            <StatsWidgetSpacer /> */}
            <StatsWidgetNumber
              key={`operational_insistence-${operational_insistence}`}
              value={operational_insistence}
              label="Insistência Operacional"
              onClick={() =>
                setFilter(filter === "operationalInsistence" ? null : "operationalInsistence")
              }
              clickable
              className={[
                "text-color-status-working",
                filter !== null && filter !== "operationalInsistence" ? "opacity-50" : "",
                operational_insistence > 0 ? "animate-highlight" : "",
              ].join(" ")}
            />
          </StatsWidget.Body>
        </StatsWidget>
      </StatsWidgets>
      <AlertsTable alerts={filteredAlerts} />
    </div>
  );
}

export { AlertsPage };
